import { Account, ItineraryVisibilityEnum } from "@city-sage/core-client";
import { Button, useRepository, Separator } from "@city-sage/aurum-avis-labs-components";
import { Calendar, UserPlus, ArrowRight, UserCheck } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Avatar } from "../Avatar";
import { Link } from "@tanstack/react-router";
import { useFollow } from "../../lib/useFollow";
import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '../../lib/QueryKeys';

interface ItineraryAuthorSectionProps {
  account: Account;
}
export function ItineraryAuthorSection({ account }: ItineraryAuthorSectionProps) {
  const { t } = useTranslation();
  const { api } = useRepository();

  // Get real follower/following data and follow functionality
  const { 
    followerCount, 
    isFollowing,
    canFollow,
    isPending: isToggling,
    toggleFollow,
    isLoading: isLoadingFollow 
  } = useFollow({ userId: account.id });

  // Get author's public itineraries count
  const itineraryParams = { 
    filter: JSON.stringify({ 
      accountId: account.id, 
      visibility: ItineraryVisibilityEnum.Public 
    }),
    // TODO this is hardcoded for now, we need to fix this
    limit: 1000,
  };
  
  const { data: itineraries, isLoading: isLoadingItineraries } = useQuery({
    queryKey: [QueryKeys.itinerary.all(itineraryParams)],
    queryFn: () => api.findAllItinerary(itineraryParams),
    enabled: !!account.id
  });

  const joinedYear = new Date(account.createdAt).getFullYear();
  const itinerariesCount = itineraries?.length || 0;
  const isLoading = isLoadingFollow || isLoadingItineraries;

  return (
    <div className="bg-white border-t border-gray-100">
      <div className="px-4 py-6">
        {/* Simple Header */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('components.author.createdBy')}</h3>
        </div>

        {/* Author Info */}
        <div className="flex items-start gap-4 mb-4">
          <Avatar 
            account={account} 
            className="w-14 h-14 border-2 border-gray-100" 
          />

          <div className="flex-1 min-w-0">
            <Link to="/users/$userId" params={{ userId: account.id }}>
              <h4 className="font-semibold text-gray-900 truncate mb-1">@{account.userName}</h4>
            </Link>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Calendar className="w-3 h-3" />
              <span>{t('components.author.joinedYear', { year: joinedYear })}</span>
            </div>
          </div>

          {/* Simple Follow Button */}
          {canFollow && (
            <Button 
              size="sm" 
              onClick={toggleFollow}
              disabled={isToggling}
              className={`h-8 px-3 ${
                isFollowing 
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300' 
                  : 'bg-primary hover:bg-primary/90 text-primary-foreground'
              }`}
            >
              {isToggling ? (
                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
              ) : (
                <>
                  {isFollowing ? (
                    <UserCheck className="w-3 h-3 mr-1" />
                  ) : (
                    <UserPlus className="w-3 h-3 mr-1" />
                  )}
                </>
              )}
              {isToggling 
                ? t('components.author.following') 
                : isFollowing 
                  ? t('components.author.following') 
                  : t('components.author.follow')
              }
            </Button>
          )}
        </div>

        {/* About */}
        {account.aboutMe && (
          <div className="mb-4">
            <p className="text-sm text-gray-700 leading-relaxed line-clamp-2">{account.aboutMe}</p>
          </div>
        )}

        {/* Simple Stats */}
        <div className="flex items-center gap-6 text-sm mb-4">
          <div className="text-center">
            <div className="font-semibold text-gray-900">
              {isLoading ? "..." : itinerariesCount.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">{t('components.author.stats.itineraries')}</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900">
              {isLoading ? "..." : followerCount.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">{t('components.author.stats.followers')}</div>
          </div>
        </div>

        <Separator className="mb-4" />

        {/* Simple More from Author */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h5 className="font-medium text-gray-900 mb-1">{t('components.author.moreFromAuthor.title')}</h5>
              <p className="text-xs text-gray-600">
                {t('components.author.moreFromAuthor.description')}
              </p>
            </div>
            <Link to="/users/$userId" params={{ userId: account.id }}>
              <Button variant="ghost" size="sm">
                {t('components.author.moreFromAuthor.viewAll')}
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
} 